import React from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  LoaderCircle,
  FileUp,
  ScanSearch,
  FileCheck2,
  AlertTriangle,
} from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import "./DocumentProcessingStep.css";
import { getExtractionJobStatus } from "./homebuyerDocumentApi";

export default function DocumentProcessingStep() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  const outlet = useOutletContext();

  const applicationId =
    location.state?.applicationId ?? outlet?.applicationId ?? null;

  const applicationApplicantId =
    location.state?.applicationApplicantId ??
    outlet?.applicationApplicantId ??
    null;
  const jobId = location.state?.jobId;

  const [status, setStatus] = React.useState("QUEUED");
  const [stage, setStage] = React.useState("PREPARING_FILES");
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!jobId) {
      setError("Missing extraction job. Please restart the process.");
      return;
    }

    let intervalId = null;
    let cancelled = false;

    async function checkStatus() {
      try {
        const accessToken = await getAccessTokenSilently();

        const data = await getExtractionJobStatus({
          jobId,
          accessToken,
        });
        console.log(data);
        if (cancelled) return;

        setStatus(data.status || "QUEUED");
        setStage(data.current_stage || "PREPARING_FILES");
        setProgress(data.progress_percentage ?? 0);

        if (data.status === "COMPLETED") {
          if (intervalId) clearInterval(intervalId);
          navigate("/hb/application/form/extracted-data", {
            replace: true,
            state: {
              applicationId,
              applicationApplicantId,
              jobId,
            },
          });
        }

        if (data.status === "FAILED") {
          if (intervalId) clearInterval(intervalId);
          setError(data.error_message || "Automatic extraction failed.");
        }
      } catch (err) {
        if (intervalId) clearInterval(intervalId);
        setError(err.message || "Failed to check extraction status.");
      }
    }

    checkStatus();
    intervalId = setInterval(checkStatus, 3000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    applicationId,
    applicationApplicantId,
    jobId,
    navigate,
    getAccessTokenSilently,
  ]);

  function getMessage() {
    if (stage === "PREPARING_FILES") {
      return "We are preparing your uploaded files for processing.";
    }
    if (stage === "CLASSIFYING_DOCUMENTS") {
      return "We are validating the uploaded documents before extraction.";
    }
    if (stage === "EXTRACTING_FIELDS") {
      return "We are extracting information from your uploaded documents.";
    }
    if (stage === "VALIDATING_OUTPUT") {
      return "We are validating the extracted values.";
    }
    if (stage === "FINALIZING") {
      return "We are preparing your prefilled review form.";
    }
    return "We are processing your documents.";
  }

  const steps = [
    {
      key: "PREPARING_FILES",
      label: "Documents received",
      done:
        [
          "PREPARING_FILES",
          "CLASSIFYING_DOCUMENTS",
          "EXTRACTING_FIELDS",
          "VALIDATING_OUTPUT",
          "FINALIZING",
        ].includes(stage) || status === "COMPLETED",
      icon: FileUp,
    },
    {
      key: "EXTRACTING_FIELDS",
      label: "AI verification and extraction",
      done:
        ["EXTRACTING_FIELDS", "VALIDATING_OUTPUT", "FINALIZING"].includes(
          stage,
        ) || status === "COMPLETED",
      icon: ScanSearch,
    },
    {
      key: "FINALIZING",
      label: "Preparing your review form",
      done: stage === "FINALIZING" || status === "COMPLETED",
      icon: FileCheck2,
    },
  ];

  function handleBack() {
    navigate("/hb/application/document-verification", {
      state: {
        applicationId,
        applicationApplicantId,
      },
    });
  }

  return (
    <div className="hb-processing-page">
      <div className="hb-processing-card">
        {!error ? (
          <>
            <div className="hb-processing-card__icon">
              <LoaderCircle size={34} className="hb-processing-spinner" />
            </div>

            <h1 className="hb-processing-card__title">
              Analyzing your documents
            </h1>

            <p className="hb-processing-card__text">{getMessage()}</p>

            <div className="hb-processing-progress">
              <div
                className="hb-processing-progress__bar"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="hb-processing-steps">
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.key}
                    className={`hb-processing-step ${step.done ? "is-done" : ""}`}
                  >
                    <div className="hb-processing-step__icon">
                      <Icon size={18} />
                    </div>
                    <div className="hb-processing-step__label">
                      {step.label}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="hb-processing-card__hint">
              This may take 1–3 minutes depending on the number and quality of
              the uploaded documents.
            </p>
          </>
        ) : (
          <>
            <div className="hb-processing-card__icon hb-processing-card__icon--error">
              <AlertTriangle size={30} />
            </div>

            <h1 className="hb-processing-card__title">
              We could not complete the extraction
            </h1>

            <p className="hb-processing-card__text">{error}</p>

            <div className="hb-processing-card__actions">
              <button
                type="button"
                className="hb-processing-btn"
                onClick={handleBack}
              >
                Back to documents
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
